<template>
  <div class="modal">
    <ModalFullBackdrop class="fenif-test">
      <div class="container">
        <h3 class="modal-title" style="color: red">{{ $t("report.title") }}</h3>

        <template v-if="fillingForm">
          <form @submit.prevent="reportAttempt">
            <div
              v-if="!$auth.check()"
              class="form-group"
              :class="{ error: $v.userEmail.$error }"
            >
              <h4 style="margin-block-end: auto">
                {{ $t("report.form.field.email") }}
              </h4>
              <input
                type="email"
                :placeholder="$t('report.form.placeholder.email')"
                class="form-control"
                v-model="reportForm.userEmail"
                @input="$v.userEmail.$touch"
                :ref="$refs.userEmail"
              />
              <template v-if="$v.userEmail.$error">
                <p class="error-message" v-if="!$v.userEmail.email">
                  {{
                    $t("validations.email", {
                      field: $t("report.form.field.email"),
                    })
                  }}
                </p>
                <p class="error-message" v-if="!$v.userEmail.required">
                  {{
                    $t("validations.required", {
                      field: $t("report.form.field.email"),
                    })
                  }}
                </p>
              </template>
            </div>

            <div class="form-group" :class="{ error: $v.reason.$error }">
              <h4 style="margin-block-end: auto">
                {{ $t("report.form.field.reason") }}
              </h4>
              <textarea
                :placeholder="$t('report.form.placeholder.reason')"
                class="form-control textarea"
                v-model="reportForm.reason"
                @input="$v.reason.$touch"
                @keyup="textAreaAdjust"
                :ref="$refs.reason"
              >
              </textarea>
              <template v-if="$v.reason.$error">
                <p class="error-message" v-if="!$v.reason.required">
                  {{
                    $t("validations.required", {
                      field: $t("report.form.field.reason"),
                    })
                  }}
                </p>
              </template>
            </div>

            <div class="footer">
              <button class="btn-login" type="submit">
                {{ $t("report.title") }}
              </button>
              <button class="cancel" @click="$emit('cancel')">
                {{ $t("dashboard.back") }}
              </button>
            </div>
          </form>
        </template>
        <template v-else>
          <h4>{{ endedRequestMessage }}</h4>
          <div class="footer">
            <button class="cancel" @click="$emit('cancel')">
              {{ $t("dashboard.back") }}
            </button>
          </div>
        </template>
      </div>
    </ModalFullBackdrop>
  </div>
</template>

<script setup lang="ts">
import {
  required,
  minLength,
  email,
  requiredIf,
  maxLength,
} from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";

import resources from "@/utils/resources";
import _NuxtApp from "~/types/NuxtApp";

const REASON_MAX_LENGTH = 500;

const $emit = defineEmits(["cancel"]);

const { $auth, $i18n: { t: $t } } = <_NuxtApp>useNuxtApp();

const { modelId, postId } = defineProps({
  modelId: {
    type: String,
    required: true,
  },
  postId: {
    type: String,
    required: true,
  },
});

const reportForm = reactive({
  userEmail: "",
  reason: "",
});
const fillingForm = ref<boolean>(true);
const endedRequestMessage = ref<string>($t("report.response_message.waiting"));
let endedRequestTimer: NodeJS.Timeout | null = null;

const $refs = <
  {
    userEmail: Ref<HTMLElement>;
    reason: Ref<HTMLElement>;
  }
>{
  userEmail: ref(),
  reason: ref(),
};

function textAreaAdjust() {
  const elem = $refs.reason.value;

  elem.style.height = "1px";
  elem.style.height = 10 + elem.scrollHeight + "px";
}

function reportAttempt() {
  $v.value.$touch();

  if ($v.value.$invalid) return;

  fillingForm.value = false;
  resources.postOption
    .report({
      userEmail: reportForm.userEmail,
      reason: reportForm.reason,
      modelId,
      postId,
    })
    .then((response: Response & { data: any }) => {
      switch (response.status) {
        case 201:
          endedRequestMessage.value = $t("report.response_message.success");
          break;

        case 422:
          endedRequestMessage.value = $t("report.response_message.bad_request");
          break;

        case 500:
          endedRequestMessage.value = $t("report.response_message.error");
          break;

        case 208:
          endedRequestMessage.value = $t("report.response_message.already_reported");
          break;

        default:
          endedRequestMessage.value = $t("report.response_message.error");
          break;
      }
    })
    .catch((error) => {
      endedRequestMessage.value = $t("report.response_message.error");
      console.log(error);
    })
    .finally(() => {
      endedRequestTimer = setTimeout(() => {
        $emit("cancel");
      }, 5000);
    });
}

const rules = {
  userEmail: {
    requiredIf: requiredIf(() => !$auth.check()),
    email,
    minLength: minLength(4),
  },
  reason: {
    required,
    maxLength: maxLength(REASON_MAX_LENGTH),
  },
};

const $v = useVuelidate(rules, reportForm);

onMounted(() => {
  if ($auth.check()) $refs.reason.value.focus();
  else $refs.userEmail.value.focus();
});

onUnmounted(() => {
  if (endedRequestTimer) clearTimeout(endedRequestTimer);
});
</script>

<style lang="scss" scoped>
.form-group.error {
  text-align: left;
  color: red;
}

.modal {
  position: absolute;
  & .picture {
    width: 5rem;
    height: 5rem;
    margin: 0 auto;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    background-color: #ffffff;
    border-radius: 50%;
    border: 3px solid var(--pallete_main_color);
  }

  & .container {
    box-sizing: border-box;
    border-radius: 1rem;
    border: solid 2px #ccc;
    padding: 1rem;

    @media (min-width: 768px) {
      min-width: 350px;
    }

    & .modal-title {
      font-size: 1.5rem;
      text-transform: uppercase;
    }

    & h3 {
      margin: 0.5rem 0;
    }

    & .footer {
      display: flex;
      justify-content: flex-end;

      & button,
      .action {
        color: inherit;
        border-radius: 0.5rem;
        margin-left: 1rem;
        font-size: 1rem;
        font-weight: bold;
        text-transform: uppercase;
        padding: 0.5rem 1rem;

        &:hover {
          color: var(--pallete_over_main_text_color);
        }
      }

      & .action {
        padding: 0;
      }

      & .action > button {
        margin-left: 0;
      }

      & button.cancel {
        @extend %fresh-btn;

        border-radius: 0.5rem;
      }
    }
  }
}

h1 {
  text-align: center;
  margin: 50px 0 80px;
}
.share-network-list {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1000px;
  margin: auto;
}
a[class^="share-network-"] {
  flex: none;
  color: #ffffff;
  background-color: #333;
  border-radius: 3px;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-content: center;
  align-items: center;
  cursor: pointer;
  margin: 0 10px 10px 0;
}
a[class^="share-network-"] .fah {
  background-color: rgba(0, 0, 0, 0.2);
  padding: 10px;
  flex: 0 1 auto;
}
a[class^="share-network-"] span {
  padding: 0 10px;
  flex: 1 1 0%;
  font-weight: 500;
}

.form-control {
  &.textarea {
    min-height: 50px;
    max-height: 150px;
  }
}
</style>
